import { useState } from 'react';

import { Button as MuiButton, Stack, Typography } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import {
  SOURCE_LABEL,
  type SourceId,
} from '@/james-playground/aa-family-claim/claim/facts';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import {
  labelOf,
  valueText,
} from '@/james-playground/aa-family-claim/claim/labels';
import {
  CARD,
  CONFIRM,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  EntryField,
  EntryToggle,
  hasProblem,
  Source,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  CAN_BE_NONE,
  known,
  shows,
  storedAs,
  str,
  suggestion,
  type Draft,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * What the claim already has, with where it came from, to confirm in one tap.
 * "Change something" turns the list into fields. When something the form
 * needs is missing, the card opens on the fields, since there is nothing yet
 * to confirm.
 */
const ConfirmCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const [draft, setDraft] = useState<Draft>(() =>
    Object.fromEntries(
      ask.entries.map((id) => [
        id,
        str(known(facts, id)) || suggestion(id, facts) || '',
      ]),
    ),
  );
  const [initial] = useState(draft);
  const ids = ask.entries.filter((id) => shows(id, ask.entries, facts, draft));
  const missing = ids.some(
    (id) => !CAN_BE_NONE.has(id) && !str(draft[id]).trim(),
  );
  const [editing, setEditing] = useState(missing);

  const sourceOf = (id: string): SourceId => facts[id]?.source ?? 'derived';
  const sources = [
    ...new Set(ids.filter((id) => str(draft[id])).map(sourceOf)),
  ];
  // "From her care profile"; a value the claim worked out says so as it is.
  const fromLine = (s: SourceId) =>
    s === 'derived'
      ? SOURCE_LABEL.derived
      : say(CONFIRM.from(SOURCE_LABEL[s]), v);

  const submitAll = (reply: string) =>
    onSubmit(
      Object.fromEntries(
        ids.map((id) => [id, storedAs(entry(id).answerType, str(draft[id]))]),
      ),
      reply,
    );

  const changed = ids.filter(
    (id) => str(draft[id]).trim() !== str(initial[id]).trim(),
  );
  const ready =
    ids.every((id) => CAN_BE_NONE.has(id) || str(draft[id]).trim()) &&
    !ids.some((id) => hasProblem(id, draft[id]));

  if (!editing)
    return (
      <CardShell
        label={say(ask.title, v)}
        onSkip={onSkip}
        actions={
          <>
            <MuiButton
              variant='contained'
              fullWidth
              onClick={() => submitAll(CONFIRM.right)}
            >
              {CONFIRM.right}
            </MuiButton>
            <MuiButton
              variant='outlined'
              fullWidth
              onClick={() => setEditing(true)}
            >
              {CONFIRM.change}
            </MuiButton>
          </>
        }
      >
        <Stack spacing={1.5}>
          {sources.length === 1 && <Source>{fromLine(sources[0])}</Source>}
          <Stack component='dl' spacing={1.25} sx={{ m: 0 }}>
            {ids.map((id) => {
              const value = str(draft[id]);
              return (
                <Stack key={id} spacing={0.25}>
                  <Typography component='dt' variant='body2'>
                    {labelOf(id, v)}
                  </Typography>
                  <Typography
                    component='dd'
                    variant='body1'
                    sx={{ m: 0, whiteSpace: 'pre-line' }}
                  >
                    {!value
                      ? CONFIRM.none
                      : entry(id).answerType === 'address'
                        ? value
                        : valueText(id, value)}
                  </Typography>
                  {sources.length > 1 && value && (
                    <Typography component='dd' variant='caption' sx={{ m: 0 }}>
                      {fromLine(sourceOf(id))}
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </CardShell>
    );

  return (
    <CardShell
      label={say(ask.title, v)}
      onSkip={onSkip}
      ready={ready}
      onContinue={() =>
        submitAll(
          changed.length
            ? `${CONFIRM.changed}: ${changed
                .map(
                  (id) =>
                    `${labelOf(id, v)} ${valueText(id, storedAs(entry(id).answerType, str(draft[id]))) || CONFIRM.none}`,
                )
                .join(', ')}`
            : CONFIRM.right,
        )
      }
      continueLabel={CARD.continue}
    >
      <Stack spacing={2}>
        {ids.map((id) =>
          entry(id).answerType === 'single_choice' ||
          entry(id).answerType === 'yes_no' ? (
            <EntryToggle
              key={id}
              id={id}
              v={v}
              question={labelOf(id, v)}
              value={str(draft[id])}
              onChange={(value) => setDraft((d) => ({ ...d, [id]: value }))}
            />
          ) : (
            <EntryField
              key={id}
              id={id}
              v={v}
              value={str(draft[id])}
              onChange={(value) => setDraft((d) => ({ ...d, [id]: value }))}
            />
          ),
        )}
      </Stack>
    </CardShell>
  );
};

ConfirmCard.displayName = 'ConfirmCard';
export default ConfirmCard;
